import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  CommandPalette,
  type CommandItem,
} from "@/components/command-palette/command-palette";
import { useCommandKShortcut } from "@/components/command-palette/use-command-k-shortcut";
import type { SearchResult } from "@/lib/search/search-service";
import { adminFetch } from "@/api";

const DEBOUNCE_MS = 150;
const HREF_META = "__href";

type SearchItem = CommandItem & { [HREF_META]: string };

export function GlobalSearch(): React.JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<SearchItem[]>([]);

  useCommandKShortcut(() => setOpen((v) => !v));

  useEffect(() => {
    if (!open) {
      setQuery("");
      setItems([]);
      return;
    }
    if (query.trim().length < 2) {
      setItems([]);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await adminFetch(
          `/api/search?q=${encodeURIComponent(query.trim())}`,
          { signal: controller.signal },
        );
        if (!res.ok) return;
        const data = (await res.json()) as { results?: SearchResult[] };
        setItems(
          (data.results ?? []).map<SearchItem>((r) => ({
            id: r.id,
            label: r.label,
            description: `${r.type.toUpperCase()} · ${r.description}`,
            keywords: [r.type, r.label],
            group: r.type,
            [HREF_META]: r.href,
          })),
        );
      } catch {
        // aborted or network error — silent
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, query]);

  const handleSelect = useCallback(
    (item: CommandItem) => {
      const href = (item as SearchItem)[HREF_META];
      if (href) navigate(href);
      setOpen(false);
    },
    [navigate],
  );

  return (
    <CommandPalette
      open={open}
      onClose={() => setOpen(false)}
      items={items}
      onSelect={handleSelect}
      onQueryChange={setQuery}
      placeholder="Search users, groups, expenses…"
      emptyLabel={
        query.trim().length < 2
          ? "Type at least 2 characters to search."
          : "No matches. Try a different term."
      }
    />
  );
}
